import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calculator, GitCompare, Layout, Users } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DeckBuilderTarget {
  descriptor: string;
  name: string;
}

export interface RelatedToolsProps {
  className?: string;
  variant?: "default" | "compact";
  /** Primary unit: profile, calculator, comparison */
  unitDescriptor?: string;
  /** On weapon/ammo pages when there is no “current” unit */
  relatedUnitDescriptor?: string;
  /** One button per division that can open the deck builder */
  deckBuilderTargets?: DeckBuilderTarget[];
  /** Set false on the unit detail page (you are already there). */
  showUnitProfileLink?: boolean;
  /** Set false when those links are shown elsewhere (e.g. unit page header). */
  showDamageCalculatorLink?: boolean;
  showUnitComparisonLink?: boolean;
}

/**
 * Consistent cross-links between unit-centric tools (detail, damage calc, comparison, deck builder).
 */
export function RelatedTools({
  className,
  variant = "default",
  unitDescriptor,
  relatedUnitDescriptor,
  deckBuilderTargets = [],
  showUnitProfileLink = true,
  showDamageCalculatorLink = true,
  showUnitComparisonLink = true,
}: RelatedToolsProps) {
  const subject = unitDescriptor ?? relatedUnitDescriptor;
  const hasDeckLinks = deckBuilderTargets.length > 0;
  if (!subject && !hasDeckLinks) return null;

  const btnClass =
    variant === "compact"
      ? "h-8 gap-1.5 text-xs"
      : "w-full justify-start gap-2 text-xs";

  const rowClass =
    variant === "compact"
      ? "flex flex-wrap items-center gap-2"
      : "flex flex-col gap-1.5";

  return (
    <div className={cn(rowClass, className)}>
      {subject ? (
        <>
          {showUnitProfileLink ? (
            <Button asChild variant="outline" size="sm" className={btnClass}>
              <Link to={`/units/${encodeURIComponent(subject)}`}>
                <Users className="h-4 w-4 shrink-0" />
                <span>Unit page</span>
              </Link>
            </Button>
          ) : null}
          {showDamageCalculatorLink ? (
            <Button asChild variant="outline" size="sm" className={btnClass}>
              <Link to={`/damage-calculator/${encodeURIComponent(subject)}`}>
                <Calculator className="h-4 w-4 shrink-0" />
                <span>Damage calculator</span>
              </Link>
            </Button>
          ) : null}
          {showUnitComparisonLink ? (
            <Button asChild variant="outline" size="sm" className={btnClass}>
              <Link to={`/unit-comparison?unit=${encodeURIComponent(subject)}`}>
                <GitCompare className="h-4 w-4 shrink-0" />
                <span>Unit comparison</span>
              </Link>
            </Button>
          ) : null}
        </>
      ) : null}
      {deckBuilderTargets.map(({ descriptor, name }) => (
        <Button key={descriptor} asChild variant="outline" size="sm" className={btnClass}>
          <Link to={`/deck-builder/${encodeURIComponent(descriptor)}`} title={name}>
            <Layout className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {deckBuilderTargets.length > 1 ? `Builder · ${name}` : "Deck builder"}
            </span>
          </Link>
        </Button>
      ))}
    </div>
  );
}
